import { useMemo, useState } from 'react';

import { ActiveDesignContext, ActiveDesignSelectionContext } from './active-design-context.js';
import { useCoreModelContext } from './core-model-context.js';

import type { ReactNode } from 'react';
import type { ActiveDesignContextValue, ActiveDesignSelection } from './active-design-context.js';

export type ActiveDesignContextProviderProps = {
	children: ReactNode;
	/** Selection to apply on mount. Ignored if it does not resolve to a design in the core model. */
	initialSelection?: ActiveDesignSelection;
};

/**
 * Owns the active-design selection — the single source of truth for which design the CAD
 * renderer shows — and provides it through {@link ActiveDesignSelectionContext} (always) and
 * {@link ActiveDesignContext} (only while the selection resolves to a design in the core model).
 * Tolerates an absent/null core model context: no selection resolves to a design until a model
 * is provided. Place it at the same level as the `CoreModelContext` provider so both the
 * renderer and page-level components (chips, search providers, etc.) share the same state.
 *
 * @param props - Component props.
 * @param props.children - Subtree that can consume the contexts.
 * @param props.initialSelection - Selection to apply on mount.
 */
export function ActiveDesignContextProvider({ children, initialSelection }: ActiveDesignContextProviderProps) {
	const coreModel = useCoreModelContext(false)?.coreModel;
	const [activeSelection, setActiveSelection] = useState<ActiveDesignSelection | null>(
		initialSelection && coreModel?.designs[initialSelection.designId] ? initialSelection : null,
	);

	const activeDesign = activeSelection ? (coreModel?.designs[activeSelection.designId] ?? null) : null;

	const selectionContextValue = useMemo(() => ({ activeSelection, setActiveSelection }), [activeSelection]);
	const activeDesignContextValue = useMemo<ActiveDesignContextValue | null>(
		() => (activeDesign ? { activeDesign } : null),
		[activeDesign],
	);

	return (
		<ActiveDesignSelectionContext value={selectionContextValue}>
			<ActiveDesignContext value={activeDesignContextValue}>{children}</ActiveDesignContext>
		</ActiveDesignSelectionContext>
	);
}
